import { Box, Button, Chip, CircularProgress, Divider, IconButton, MenuItem, TextField, Typography, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import { FormattedMessage, useIntl } from "react-intl";
import { useEffect, useState } from "react";
import Fetch from "../services/Fetch";
import { useConstants } from "../hooks/UseConstants";

function SummaryCard({ icon, label, value, theme }) {
    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
            }}
        >
            <Box
                sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 2,
                    bgcolor: "#eff6ff",
                    color: "#0D358C",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography fontWeight={800} sx={{ overflowWrap: "anywhere" }}>{value}</Typography>
            </Box>
        </Box>
    );
}

function TeacherDetails({ onClickClose, teacher, onUpdated, setSnackBar }) {
    const { host, language } = useConstants();
    const today = new Date();
    const birthDate = teacher.birth_date ? new Date(teacher.birth_date) : null;
    const [commission, setCommission] = useState(teacher.teacher_commission_percentage ?? '');
    const [saving, setSaving] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [resettingPassword, setResettingPassword] = useState(false);
    const [profileSaving, setProfileSaving] = useState(false);
    const [firstName, setFirstName] = useState(teacher.first_name || "");
    const [lastName, setLastName] = useState(teacher.last_name || "");
    const [email, setEmail] = useState(teacher.email || "");
    const [phoneCode, setPhoneCode] = useState(teacher.phone_code || "");
    const [phone, setPhone] = useState(teacher.phone || "");
    const [experienceYears, setExperienceYears] = useState(teacher.experience_years ?? "");
    const [majorId, setMajorId] = useState(teacher.major?.id ? String(teacher.major.id) : "");
    const [academicDegreeId, setAcademicDegreeId] = useState(teacher.academic_degree?.id ? String(teacher.academic_degree.id) : "");
    const [specializationId, setSpecializationId] = useState(teacher.specialization?.id ? String(teacher.specialization.id) : "");
    const [majors, setMajors] = useState([]);
    const [academicDegrees, setAcademicDegrees] = useState([]);
    const [specializations, setSpecializations] = useState([]);

    const theme = useTheme();
    const intl = useIntl();
    const isAr = language === 'ar';
    const age = birthDate && !Number.isNaN(birthDate.getTime()) ? today.getFullYear() - birthDate.getFullYear() : "-";
    const statusLabel = teacher.is_active ? (isAr ? 'مفعّل' : 'Active') : (isAr ? 'بانتظار التفعيل' : 'Pending approval');
    const formatDate = (value) => {
        if (!value) return isAr ? "غير متوفر" : "Unavailable";
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return value;
        return new Intl.DateTimeFormat(isAr ? "ar-EG" : "en-US", { dateStyle: "medium" }).format(parsed);
    };

    useEffect(() => {
        setFirstName(teacher.first_name || "");
        setLastName(teacher.last_name || "");
        setEmail(teacher.email || "");
        setPhoneCode(teacher.phone_code || "");
        setPhone(teacher.phone || "");
        setExperienceYears(teacher.experience_years ?? "");
        setMajorId(teacher.major?.id ? String(teacher.major.id) : "");
        setAcademicDegreeId(teacher.academic_degree?.id ? String(teacher.academic_degree.id) : "");
        setSpecializationId(teacher.specialization?.id ? String(teacher.specialization.id) : "");
    }, [teacher]);

    useEffect(() => {
        const loadOptions = async () => {
            const [majorsResult, degreesResult, specializationsResult] = await Promise.all([
                Fetch(`${host}/majors?per_page=200`, 'GET', null),
                Fetch(`${host}/academic-degrees?per_page=100`, 'GET', null),
                Fetch(`${host}/teacher-specializations?per_page=100`, 'GET', null),
            ]);

            if (majorsResult.status === 200) {
                const items = majorsResult.data?.data?.data || majorsResult.data?.data || [];
                setMajors(Array.isArray(items) ? items : []);
            }
            if (degreesResult.status === 200) {
                const items = degreesResult.data?.data?.data || degreesResult.data?.data || [];
                setAcademicDegrees(Array.isArray(items) ? items : []);
            }
            if (specializationsResult.status === 200) {
                const items = specializationsResult.data?.data?.data || specializationsResult.data?.data || [];
                setSpecializations(Array.isArray(items) ? items : []);
            }
        };

        loadOptions();
    }, [host]);

    const saveCommission = async () => {
        setSaving(true);
        const formData = new FormData();
        if (commission !== '') formData.append('teacher_commission_percentage', commission);
        const result = await Fetch(`${host}/admin/users/${teacher.id}/update`, 'POST', formData);
        if (result.status === 200) {
            setSnackBar?.('success', isAr ? 'تم حفظ نسبة العمولة' : 'Commission saved');
            onUpdated?.(result.data.data);
        } else {
            setSnackBar?.('error', result.data?.errors?.[0] || result.data?.message || 'Error');
        }
        setSaving(false);
    };

    const toggleActive = async () => {
        setToggling(true);
        const result = await Fetch(`${host}/admin/users/${teacher.id}/active`, 'POST', {});
        if (result.status === 200) {
            setSnackBar?.('success', isAr ? 'تم تحديث حالة الحساب' : 'Account status updated');
            onUpdated?.({ ...teacher, is_active: !teacher.is_active });
        } else {
            setSnackBar?.('error', result.data?.errors?.[0] || result.data?.message || 'Error');
        }
        setToggling(false);
    };

    const resetPassword = async () => {
        if (!newPassword || !confirmPassword) {
            setSnackBar?.('error', isAr ? 'أدخل كلمة المرور الجديدة وتأكيدها' : 'Enter the new password and confirmation');
            return;
        }

        if (newPassword !== confirmPassword) {
            setSnackBar?.('error', isAr ? 'كلمتا المرور غير متطابقتين' : 'Passwords do not match');
            return;
        }

        setResettingPassword(true);
        const formData = new FormData();
        formData.append('password', newPassword);
        formData.append('password_confirmation', confirmPassword);

        const result = await Fetch(`${host}/admin/users/${teacher.id}/password/update`, 'POST', formData);
        if (result.status === 200) {
            setNewPassword("");
            setConfirmPassword("");
            setSnackBar?.('success', isAr ? 'تمت إعادة تعيين كلمة المرور' : 'Password reset successfully');
        } else {
            setSnackBar?.('error', result.data?.errors?.[0] || result.data?.message || 'Error');
        }
        setResettingPassword(false);
    };

    const saveProfile = async () => {
        if (!firstName.trim() || !lastName.trim() || !email.trim()) {
            setSnackBar?.('error', isAr ? 'أكمل الاسم والبريد الإلكتروني أولاً' : 'Complete the name and email first');
            return;
        }

        setProfileSaving(true);
        const formData = new FormData();
        formData.append('first_name', firstName.trim());
        formData.append('last_name', lastName.trim());
        formData.append('email', email.trim());
        if (phoneCode !== '') formData.append('phone_code', phoneCode);
        if (phone !== '') formData.append('phone', phone);
        if (majorId !== '') formData.append('major_id', majorId);
        if (academicDegreeId !== '') formData.append('academic_degree_id', academicDegreeId);
        if (specializationId !== '') formData.append('specialization_id', specializationId);
        if (experienceYears !== '') formData.append('experience_years', experienceYears);

        const result = await Fetch(`${host}/admin/users/${teacher.id}/update`, 'POST', formData);
        if (result.status === 200) {
            setSnackBar?.('success', isAr ? 'تم حفظ بيانات المدرس' : 'Teacher profile saved');
            onUpdated?.(result.data.data);
        } else {
            setSnackBar?.('error', result.data?.errors?.[0] || result.data?.message || 'Error');
        }
        setProfileSaving(false);
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.paper,
                width: { xs: "94%", sm: "86%", md: "70%" },
                maxWidth: 980,
                maxHeight: "92vh",
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
                display: "flex",
                flexDirection: "column",
            }}
            dir={isAr ? "rtl" : "ltr"}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    background: "linear-gradient(135deg, #0D358C 0%, #2563eb 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <PersonOutlineOutlinedIcon sx={{ fontSize: 32 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={800}><FormattedMessage id='teacher_details' /></Typography>
                        <Typography variant="body2" sx={{ opacity: 0.88 }}>
                            {isAr ? "مراجعة بيانات المعلم وإدارة حالة الحساب والعمولة." : "Review teacher information and manage account state and commission."}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClickClose} sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 3, overflowY: "auto" }} className="none-view-scroll">
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ width: 72, height: 72, borderRadius: "50%", bgcolor: "#e2e8f0", color: "#475569", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800 }}>
                            {teacher.first_name?.charAt(0)}{teacher.last_name?.charAt(0)}
                        </Box>
                        <Box>
                            <Typography variant="h5" fontWeight={900} sx={{ color: "#0D358C" }}>
                                {teacher.first_name} {teacher.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{teacher.email}</Typography>
                        </Box>
                    </Box>
                    <Chip label={statusLabel} color={teacher.is_active ? "success" : "warning"} variant="outlined" />
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2, mb: 3 }}>
                    <SummaryCard icon={<SchoolOutlinedIcon />} label={intl.formatMessage({ id: "education_specialization" })} value={language === 'en' ? teacher.major?.name_en : teacher.major?.name_ar || "-"} theme={theme} />
                    <SummaryCard icon={<SchoolOutlinedIcon />} label={intl.formatMessage({ id: "academic_degree" })} value={language === 'en' ? teacher.academic_degree?.name_en : teacher.academic_degree?.name_ar || "-"} theme={theme} />
                    <SummaryCard icon={<CalendarMonthOutlinedIcon />} label={intl.formatMessage({ id: "age" })} value={age} theme={theme} />
                    <SummaryCard icon={<PercentOutlinedIcon />} label={intl.formatMessage({ id: "experience_years" })} value={teacher.experience_years ?? "-"} theme={theme} />
                    <SummaryCard icon={<SchoolOutlinedIcon />} label={intl.formatMessage({ id: "specialization" })} value={language === 'en' ? teacher.specialization?.name_en : teacher.specialization?.name_ar || "-"} theme={theme} />
                    <SummaryCard icon={<AlternateEmailOutlinedIcon />} label={intl.formatMessage({ id: "email" })} value={teacher.email || "-"} theme={theme} />
                    <SummaryCard icon={<PersonOutlineOutlinedIcon />} label={intl.formatMessage({ id: "phone" })} value={`${teacher.phone_code || ""} ${teacher.phone || ""}`.trim() || "-"} theme={theme} />
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ p: 2.25, borderRadius: 3, border: "1px solid", borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0", bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc", mb: 2 }}>
                    <Typography fontWeight={800} sx={{ color: "#0D358C", mb: 1.5 }}>
                        {isAr ? "تعديل بيانات المدرس" : "Edit teacher profile"}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 2 }}>
                        <TextField fullWidth size="small" label={intl.formatMessage({ id: "first_name" })} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                        <TextField fullWidth size="small" label={intl.formatMessage({ id: "last_name" })} value={lastName} onChange={(e) => setLastName(e.target.value)} />
                        <TextField fullWidth size="small" label={intl.formatMessage({ id: "email" })} value={email} onChange={(e) => setEmail(e.target.value)} />
                        <TextField fullWidth size="small" label={isAr ? "رمز الدولة" : "Country code"} value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} />
                        <TextField fullWidth size="small" label={intl.formatMessage({ id: "phone" })} value={phone} onChange={(e) => setPhone(e.target.value)} />
                        <TextField fullWidth size="small" type="number" label={intl.formatMessage({ id: "experience_years" })} value={experienceYears} onChange={(e) => setExperienceYears(e.target.value)} inputProps={{ min: 0 }} />
                        <TextField select fullWidth size="small" label={intl.formatMessage({ id: "education_specialization" })} value={majorId} onChange={(e) => setMajorId(e.target.value)}>
                            <MenuItem value="">{isAr ? "بدون تحديد" : "Not set"}</MenuItem>
                            {majors.map((item) => (
                                <MenuItem key={item.id} value={item.id}>{language === 'en' ? item.name_en : item.name_ar}</MenuItem>
                            ))}
                        </TextField>
                        <TextField select fullWidth size="small" label={intl.formatMessage({ id: "academic_degree" })} value={academicDegreeId} onChange={(e) => setAcademicDegreeId(e.target.value)}>
                            <MenuItem value="">{isAr ? "بدون تحديد" : "Not set"}</MenuItem>
                            {academicDegrees.map((item) => (
                                <MenuItem key={item.id} value={item.id}>{language === 'en' ? item.name_en : item.name_ar}</MenuItem>
                            ))}
                        </TextField>
                        <TextField select fullWidth size="small" label={intl.formatMessage({ id: "specialization" })} value={specializationId} onChange={(e) => setSpecializationId(e.target.value)} sx={{ gridColumn: { xs: "span 1", md: "span 2" } }}>
                            <MenuItem value="">{isAr ? "بدون تحديد" : "Not set"}</MenuItem>
                            {specializations.map((item) => (
                                <MenuItem key={item.id} value={item.id}>{language === 'en' ? item.name_en : item.name_ar}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {isAr ? "يمكنك تعديل بيانات الحساب الأكاديمية وبيانات التواصل مباشرة من هذه النافذة." : "You can update academic and contact fields directly from this dialog."}
                    </Typography>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                    <Box sx={{ p: 2.25, borderRadius: 3, border: "1px solid", borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0", bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc" }}>
                        <Typography fontWeight={800} sx={{ color: "#0D358C", mb: 1.5 }}>
                            {isAr ? "إعدادات الحساب" : "Account settings"}
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            label={isAr ? 'حالة الحساب' : 'Account status'}
                            value={statusLabel}
                            InputProps={{ readOnly: true }}
                            sx={{ mb: 1.5 }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label={isAr ? 'تاريخ الميلاد' : 'Birth date'}
                            value={formatDate(teacher.birth_date)}
                            InputProps={{ readOnly: true }}
                        />
                    </Box>

                    <Box sx={{ p: 2.25, borderRadius: 3, border: "1px solid", borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0", bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc" }}>
                        <Typography fontWeight={800} sx={{ color: "#0D358C", mb: 1.5 }}>
                            {isAr ? "العمولة" : "Commission"}
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label={isAr ? 'نسبة عمولة المدرس (%)' : 'Teacher commission (%)'}
                            value={commission}
                            onChange={(e) => setCommission(e.target.value)}
                            inputProps={{ min: 0, max: 100 }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {isAr ? "يمكنك تعديل نسبة العمولة ثم حفظها مباشرة من هذه النافذة." : "You can update the commission percentage and save it directly from this dialog."}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ mt: 2, p: 2.25, borderRadius: 3, border: "1px solid", borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0", bgcolor: theme.palette.mode === "dark" ? "background.default" : "#f8fafc" }}>
                    <Typography fontWeight={800} sx={{ color: "#0D358C", mb: 1.5 }}>
                        {isAr ? "إعادة تعيين كلمة المرور" : "Reset password"}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="password"
                            label={isAr ? "كلمة المرور الجديدة" : "New password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            type="password"
                            label={isAr ? "تأكيد كلمة المرور" : "Confirm password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {isAr ? "يستخدم هذا الإجراء للوصول الإداري عند نسيان كلمة المرور أو دعم المستخدم." : "Use this administrative action when the teacher forgets their password or needs support access."}
                    </Typography>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end", mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={saveProfile}
                        disabled={profileSaving}
                        startIcon={profileSaving ? <CircularProgress size={18} color="inherit" /> : <PersonOutlineOutlinedIcon />}
                        sx={{ minWidth: 190, bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                    >
                        {isAr ? "حفظ بيانات المدرس" : "Save teacher profile"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={resetPassword}
                        disabled={resettingPassword}
                        startIcon={resettingPassword ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineOutlinedIcon />}
                        sx={{ minWidth: 190 }}
                    >
                        {isAr ? "حفظ كلمة المرور الجديدة" : "Save new password"}
                    </Button>
                    <Button
                        variant="outlined"
                        color={teacher.is_active ? 'warning' : 'success'}
                        onClick={toggleActive}
                        disabled={toggling}
                        startIcon={toggling ? <CircularProgress size={18} color="inherit" /> : (teacher.is_active ? <PauseCircleOutlineOutlinedIcon /> : <CheckCircleOutlineOutlinedIcon />)}
                        sx={{ minWidth: 170 }}
                    >
                        {teacher.is_active
                            ? (isAr ? 'إيقاف الحساب' : 'Deactivate')
                            : (isAr ? 'تفعيل الحساب' : 'Activate account')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={saveCommission}
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <PercentOutlinedIcon />}
                        sx={{ minWidth: 170, bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                    >
                        {isAr ? 'حفظ العمولة' : 'Save commission'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default TeacherDetails;
